// Dash Rent rentals (`/rent/mgmt/v1/rental`).
//
// `rental` is the single commercial entity (TRD B0/P1) — it merges the old
// handover_booking with the rental agreement and is distinguished by `status`.
// Money is integer Rupiah; dates/times are WIB.

import { RepossessionStatus } from './common';
import { DriverOutstandingStatus } from './outstanding';
import {
  ApiPagination,
  PackageContentBlock,
  PackageStatus,
  PackageVehicleReason,
  RentType,
} from './package';

/**
 * The COMMERCIAL contract only. Where the unit physically is — waiting at the
 * hub, out with the driver, returned — is `HandoverStatus`, a separate
 * dimension. A rental is ACTIVE from the moment the DP lands until the contract
 * ends, whatever the unit is doing, so `status` alone can never tell you
 * whether a bike is on the road. Read the pair.
 */
export enum RentalStatus {
  /** Requested with no unit in stock. */
  PRE_ORDER = 'PRE_ORDER',
  /** Unit reserved from stock, DP not paid yet. */
  REQUEST_BOOKING = 'REQUEST_BOOKING',
  /** DP paid and a unit assigned: the contract is live. */
  ACTIVE = 'ACTIVE',
  /** The unit came back; `terminationReason` says why. */
  ENDED = 'ENDED',
  /** RTO only: the tenor filled and the unit became the driver's property. */
  COMPLETED = 'COMPLETED',
  /** Never became live, or died before pickup. */
  CANCELLED = 'CANCELLED',
}

export const RENTAL_STATUS_LABEL: Record<RentalStatus, string> = {
  [RentalStatus.PRE_ORDER]: 'Pre Order',
  [RentalStatus.REQUEST_BOOKING]: 'Request Booking',
  [RentalStatus.ACTIVE]: 'Active',
  [RentalStatus.ENDED]: 'Selesai',
  [RentalStatus.COMPLETED]: 'Lunas (RTO)',
  [RentalStatus.CANCELLED]: 'Cancelled',
};

/**
 * Where the unit physically is. Shared with the legacy FMS handover flow, which
 * only ever uses ACTIVE and RETURNED — the other rungs are Dash Rent's.
 */
export enum HandoverStatus {
  /** Paid and assigned, but still at the hub. NOT deployed. */
  WAITING_HANDOVER = 'WAITING_HANDOVER',
  /** Out with the driver. The only status that counts as deployed. */
  ACTIVE = 'ACTIVE',
  /** A return is scheduled; the unit is still in the driver's hands. */
  WAITING_RETURN = 'WAITING_RETURN',
  RETURNED = 'RETURNED',
  /** RTO tenor filled — sold into ownership, never coming back. */
  RELEASED = 'RELEASED',
  /** Slot lapsed, never collected. */
  NO_SHOW = 'NO_SHOW',
  /** Cancelled after the DP landed but before pickup. */
  CANCELLED = 'CANCELLED',
}

export const HANDOVER_STATUS_LABEL: Record<HandoverStatus, string> = {
  [HandoverStatus.WAITING_HANDOVER]: 'Menunggu Serah Terima',
  [HandoverStatus.ACTIVE]: 'Di Mitra',
  [HandoverStatus.WAITING_RETURN]: 'Menunggu Pengembalian',
  [HandoverStatus.RETURNED]: 'Dikembalikan',
  [HandoverStatus.RELEASED]: 'Lepas (RTO)',
  [HandoverStatus.NO_SHOW]: 'No Show',
  [HandoverStatus.CANCELLED]: 'Dibatalkan',
};

/**
 * The rental status where the meter runs and the escalation ladder climbs. Ops
 * actions on a live rental must accept every member.
 *
 * One member now, and that is the point: a scheduled return no longer changes
 * `rental.status`, so it cannot be forgotten from this list the way
 * WAITING_RETURN once could — which is how a driver used to be able to stop the
 * meter by scheduling and cancelling a return.
 */
export const LIVE_RENTAL_STATUSES: readonly RentalStatus[] = [
  RentalStatus.ACTIVE,
];

/** Derived DP summary; the charge ledger stays the source of truth (B0). */
export enum RentalPaymentStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export const RENTAL_PAYMENT_STATUS_LABEL: Record<RentalPaymentStatus, string> =
  {
    [RentalPaymentStatus.PENDING_PAYMENT]: 'Pending Payment',
    [RentalPaymentStatus.PAID]: 'Paid',
    [RentalPaymentStatus.CANCELLED]: 'Cancelled',
    [RentalPaymentStatus.REFUNDED]: 'Refunded',
  };

/** Late-payment ladder (TRD L3). */
export enum EscalationStatus {
  NORMAL = 'NORMAL',
  WARNED = 'WARNED',
  SHUTDOWN_PENDING = 'SHUTDOWN_PENDING',
  SHUTDOWN = 'SHUTDOWN',
  REPO_REQUESTED = 'REPO_REQUESTED',
  HOLD = 'HOLD',
}

/**
 * Named after the treatment, not the state machine. The three rungs ops works
 * carry three different jobs — watch it, go switch it off, go collect it — and
 * "Shutdown Pending" vs "Shutdown" vs "Repo Requested" made them read as one
 * escalating shade of the same thing. The enum values on the wire are
 * unchanged; only what ops reads is.
 */
export const ESCALATION_STATUS_LABEL: Record<EscalationStatus, string> = {
  [EscalationStatus.NORMAL]: 'Normal',
  [EscalationStatus.WARNED]: 'Diperingatkan',
  [EscalationStatus.SHUTDOWN_PENDING]: 'Wajib dimatikan',
  [EscalationStatus.SHUTDOWN]: 'Sudah dimatikan',
  [EscalationStatus.REPO_REQUESTED]: 'Perlu ditarik',
  [EscalationStatus.HOLD]: 'Eskalasi dijeda',
};

/**
 * One palette for the ladder, so a pin on the map and a pill in a table never
 * describe the same rung in two different colours. REPO_REQUESTED is a
 * deliberately different hue rather than a darker red: it is the only rung
 * where someone drives out to the unit, and at a glance on a map full of dots
 * that difference has to survive being 16px wide.
 */
export const ESCALATION_STATUS_COLOR: Record<EscalationStatus, string> = {
  [EscalationStatus.NORMAL]: '#3B82F6',
  [EscalationStatus.WARNED]: '#F6B51E',
  [EscalationStatus.SHUTDOWN_PENDING]: '#FA7319',
  [EscalationStatus.SHUTDOWN]: '#FB3748',
  [EscalationStatus.REPO_REQUESTED]: '#7A0C2E',
  [EscalationStatus.HOLD]: '#6B7280',
};

/**
 * Distinguishes the fates of a COMPLETED rental.
 *
 * `COMPLETED` is the odd one out and the reason this enum is easy to misread:
 * the backend stamps it on *every* rental that finishes normally, so a
 * non-null `terminationReason` does NOT mean the rental was terminated early.
 * Only the other members describe an abnormal ending — see
 * `isAbnormalTermination`.
 */
export enum TerminationReason {
  DRIVER_REQUEST = 'DRIVER_REQUEST',
  UNIT_FAILURE = 'UNIT_FAILURE',
  REPOSSESSION = 'REPOSSESSION',
  OPS_DECISION = 'OPS_DECISION',
  /** The contract simply ran its course — the normal, unremarkable ending. */
  COMPLETED = 'COMPLETED',
  /** The reservation lapsed before it ever became a live rental. */
  EXPIRED = 'EXPIRED',
  /** DP was paid but the unit was never collected; the DP goes back in full. */
  NO_SHOW = 'NO_SHOW',
  /** RTO tenor filled and settled — the unit is the driver's now. */
  TENOR_COMPLETE = 'TENOR_COMPLETE',
}

export const TERMINATION_REASON_LABEL: Record<TerminationReason, string> = {
  [TerminationReason.DRIVER_REQUEST]: 'Permintaan Mitra',
  [TerminationReason.UNIT_FAILURE]: 'Unit Bermasalah',
  [TerminationReason.REPOSSESSION]: 'Repossession',
  [TerminationReason.OPS_DECISION]: 'Keputusan Ops',
  [TerminationReason.COMPLETED]: 'Selesai Normal',
  [TerminationReason.EXPIRED]: 'Kedaluwarsa',
  [TerminationReason.NO_SHOW]: 'Tidak Diambil',
  [TerminationReason.TENOR_COMPLETE]: 'Tenor Lunas',
};

/**
 * Whether a reason describes a rental that ended *badly*, which is the only
 * case the UI paints as "Terminated".
 *
 * Guarding on `terminationReason` alone would mark every normally-finished
 * rental as terminated, because the backend always stamps `COMPLETED`.
 */
export const isAbnormalTermination = (
  reason: TerminationReason | null | undefined
): boolean => Boolean(reason) && reason !== TerminationReason.COMPLETED;

// The rental detail no longer ships `charges[]` — the two figures it needs
// arrive as `depositInstallment` and `tenor`, payment rows live on
// /collections, and the ledger itself is its own paginated endpoint
// (`GET /rental/:id/charges`, typed further down).

export interface RentalDriver {
  id: number;
  name: string;
  code: string;
}

/** Null while the rental is still PRE_ORDER (no unit assigned yet). */
export interface RentalVehicle {
  id: string;
  platNomor: string;
  modelName: string;
  oemName: string;
  /**
   * Detail only — the list stops at plate + model. Present since the rental
   * detail was enriched; still optional so a list row type-checks.
   */
  vin?: string | null;
  engineNumber?: string | null;
  imei?: string | null;
  color?: string | null;
  /** Current fleet status of the unit, e.g. ACTIVE / IDLE / BROKEN. */
  status?: string | null;
  imageURL?: string | null;
}

export interface RentalPackageRef {
  id: string;
  name: string;
  contractType: RentType;
}

export interface RentalHub {
  id: number;
  name: string;
}

export interface RentalSlot {
  date: string;
  startTime: string;
  endTime?: string;
}

export interface RentalArrears {
  days: number;
  amount: number;
  /**
   * ISO instant when today's unpaid charge turns into arrears, anchored at the
   * handover's time-of-day. Null + days > 0 means already in arrears; null +
   * days 0 means nothing due.
   */
  startAt?: string | null;
  /** True within the server-configured warning window before `startAt`. */
  warningActive?: boolean;
}

/** Row shape from `GET /rental`. */
/**
 * Outcome of a refund disbursement, mirroring core-payment's payout lifecycle.
 * NOT_INSTRUCTED is the one that needs ops — no payout was ever sent — and the
 * only status the retry endpoint accepts.
 */
export enum RefundPayoutStatus {
  /** No payout could be instructed at all. Retryable. */
  NOT_INSTRUCTED = 'NOT_INSTRUCTED',
  /** Instructed, outcome not back yet. */
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  /** A payout WAS sent and the bank rejected it. Not retryable from here. */
  FAILED = 'FAILED',
}

export const REFUND_PAYOUT_STATUS_LABEL: Record<RefundPayoutStatus, string> = {
  [RefundPayoutStatus.NOT_INSTRUCTED]: 'Refund Tertahan',
  [RefundPayoutStatus.PENDING]: 'Refund Diproses',
  [RefundPayoutStatus.COMPLETED]: 'Refund Selesai',
  [RefundPayoutStatus.FAILED]: 'Refund Gagal',
};

/**
 * Worst refund state across the rental's Refund Payouts
 * (NOT_INSTRUCTED > FAILED > PENDING > COMPLETED), or null when nothing was
 * refunded. `payoutID` is the retry endpoint's path param.
 */
export interface RentalRefundSummary {
  payoutID: string;
  /** Null for a deposit paid entirely by installments — it has no collection. */
  collectionID: string | null;
  payoutStatus: RefundPayoutStatus;
  /** NET of the Rp 2.500 disbursement fee. Null on rows that predate it. */
  amount: number | null;
}

export interface RentalListItem {
  id: string;
  /** Human-readable rental code, e.g. "RENOD-260730-A7K2M9". */
  code?: string;
  /** Some endpoints return uppercase ID (Drizzle PK casing) — normalised in
   *  the service layer, kept here so the raw response still type-checks. */
  ID?: string;
  status: RentalStatus;
  /**
   * Where the unit is. Null before the booking has a handover row at all
   * (PRE_ORDER / REQUEST_BOOKING). Read WITH `status`: the rental is ACTIVE
   * whether the bike is at the hub or on the road, so this is the only field
   * that tells the two apart.
   */
  handoverStatus: HandoverStatus | null;
  /**
   * Replaces the old `paymentStatus` enum. Sitting beside `status` that enum
   * read as a second, equally authoritative lifecycle; the only question the
   * UI ever asked of it was "has the DP landed". Money detail lives in
   * `GET /rental/:id/collections`.
   */
  dpPaid: boolean;
  escalationStatus: EscalationStatus;
  driver: RentalDriver;
  vehicle: RentalVehicle | null;
  package: RentalPackageRef;
  hub: RentalHub;
  /** The planned PICKUP (date + window), from the handover's slot. */
  slot: RentalSlot | null;
  /**
   * The planned RETURN — off the handover, which owns the return plan. Null
   * until a return is scheduled. Distinct from `endDate`, the actual end: a
   * Waiting Return row has this and not that.
   */
  scheduledReturnDate: string | null;
  handoverDate: string | null;
  endDate: string | null;
  prepaidBalance: number;
  arrears: RentalArrears;
  /** Null unless a refund was recorded — see RentalRefundSummary. */
  refund: RentalRefundSummary | null;
  /** The newest case on this rental's handover that is not CLOSED yet. */
  repossession: RentalRepossessionLink | null;
  /**
   * Jeda Tagihan is open: no daily charge is minted and `escalationStatus` is
   * frozen, not live. Optional until every backend ships it.
   */
  billingPaused?: boolean;
  terminationReason: TerminationReason | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Jeda Tagihan — pausing a rental's daily billing while the unit is repaired
// ---------------------------------------------------------------------------

export type BillingPauseReason = 'VEHICLE_REPAIR' | 'OTHER';

export const BILLING_PAUSE_REASON_LABEL: Record<BillingPauseReason, string> = {
  VEHICLE_REPAIR: 'Perbaikan unit',
  OTHER: 'Lainnya',
};

/** One pause. `endDate` is the first billed day again (exclusive); null = open. */
export interface RentalBillingPause {
  ID: string;
  rentalID: string;
  startDate: string;
  endDate: string | null;
  reason: BillingPauseReason;
  maintenanceID: string | null;
  maintenanceCode: string | null;
  maintenanceStatus: string | null;
  notes: string | null;
  /** OPS (resumed from FMS) or RETURN (closed by the unit's return). */
  endedBySource: string | null;
  pausedBy: string;
  resumedBy: string | null;
  createdAt: string;
  resumedAt: string | null;
}

export interface PauseBillingBody {
  reason: BillingPauseReason;
  maintenanceID?: string;
  startDate: string;
  notes?: string;
  /** true: the modal's preview — same validation and numbers, nothing written. */
  dryRun?: boolean;
}

export interface PauseBillingResult {
  rentalID: string;
  dryRun: boolean;
  pauseID: string | null;
  startDate: string;
  /** startDate may take [earliestStartDate, today]. */
  earliestStartDate: string;
  today: string;
  cancelledCharges: { count: number; amount: number };
  paidDaysKept: number;
  /** Forfeited if it lands inside the pause. */
  nextFreeDay: string | null;
  escalation: { status: EscalationStatus; daysLate: number };
  repossession: { code: string; status: string } | null;
}

export interface RentalRepossessionLink {
  ID: string;
  code: string;
  status: RepossessionStatus;
  severity: string;
}

// ---------------------------------------------------------------------------
// Collections — payment history, now its own paginated endpoint
// ---------------------------------------------------------------------------

/** What the money was for. */
export enum CollectionPurpose {
  DP = 'DP',
  PREPAY = 'PREPAY',
}

export const COLLECTION_PURPOSE_LABEL: Record<CollectionPurpose, string> = {
  [CollectionPurpose.DP]: 'Uang Muka',
  [CollectionPurpose.PREPAY]: 'Prepay',
};

export enum CollectionStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  /**
   * The QR lapsed unpaid: core-payment expired it, a PREPAY passed its 24h, or
   * a DP's booking deadline passed.
   */
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  /**
   * Superseded by a payment-method switch, the booking was called off by the
   * driver or ops, or an unpaid rent payment was withdrawn (CANCEL_PAYMENT).
   * None is a failure, and a lapsed deadline reads EXPIRED.
   */
  CANCELLED = 'CANCELLED',
}

/** Row from `GET /rental/:id/collections`. */
export interface RentalCollection {
  id: string;
  /** Transaction code, e.g. "TRX-260730-A7K2M9" — distinct from rental.code. */
  code: string;
  /** What the driver paid, already net of `discountAmount`. */
  amount: number;
  /** The rent voucher used at pay. Absent from backends that predate it. */
  couponCode?: string | null;
  discountAmount?: number;
  /** Free text: QRIS, or a wallet channel code. */
  method: string;
  /** PSP reference from the capture webhook. Null until the payment settles. */
  webhookEventId: string | null;
  purpose: CollectionPurpose;
  status: CollectionStatus;
  daysRequested: number;
  /**
   * Null unless a refund was recorded, and present even when no payout was
   * instructed — unlike the driver-facing history, which hides the block until
   * `refundedAt` is set. `payoutStatus` separates the two here.
   */
  refund: {
    /** The retry endpoint's path param. Absent from backends that predate it. */
    payoutID?: string;
    /** NET of the Rp 2.500 disbursement fee. */
    amount: number;
    /** What was instructed. Null on rows blocked before the column existed. */
    grossAmount: number | null;
    /** When the payout was INSTRUCTED, not when it landed. */
    refundedAt: string | null;
    payoutStatus: RefundPayoutStatus | null;
  } | null;
  createdAt: string;
  /** Null unless PAID. */
  paidAt: string | null;
}

/**
 * `POST /refund-payouts/:payoutID/retry` — the ops drain for refunds that are
 * owed but not moving. NOT_INSTRUCTED replays under its own id; FAILED mints a
 * new attempt, so `payoutID` here can differ from the one asked for. The
 * COMPLETED/FAILED outcome arrives later by event, so callers re-read.
 */
export interface RetryRefundResponse {
  payoutID: string;
  rentalID: string;
  collectionID: string | null;
  attemptNo: number;
  payoutStatus: RefundPayoutStatus;
  /** Instructed vs what reaches the driver — they differ by the flat fee. */
  gross: number;
  net: number;
  refundAt: string;
}

export interface GetCollectionsParams {
  page?: number;
  size?: number;
  /** Comma-joined server-side; multiple values allowed. */
  status?: CollectionStatus[];
  purpose?: CollectionPurpose[];
  /**
   * Filters `createdAt`, not `paidAt` — paidAt is null for PENDING, EXPIRED
   * and FAILED, so filtering on it would silently drop every failed payment
   * from the range, which is usually what someone opening this screen wants
   * to see.
   */
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

/**
 * One settled payment on the dashboard's "Pembayaran Hari Ini" feed — the same
 * collection row as `RentalCollection`, plus the rental identity, because this
 * list spans rentals instead of sitting inside one.
 */
export interface PaidCollectionItem {
  id: string;
  code: string;
  rental: { id: string; code: string };
  driver: { name: string | null; code: string | null };
  /** Null while no unit is assigned — a PRE_ORDER pays its DP before that. */
  vehicle: { platNomor: string; modelName: string; oemName: string } | null;
  amount: number;
  /** Fee actually charged on top of `amount`; 0 while the discount is 100%. */
  adminFeeCharged: number;
  /** The rent voucher used at pay; `amount` is already net of it. */
  couponCode?: string | null;
  discountAmount?: number;
  method: string;
  purpose: CollectionPurpose;
  paidAt: string | null;
}

export interface PaidCollectionsPage extends RentalPaginated<PaidCollectionItem> {
  /** Totals over the whole window, not just this page. */
  summary: {
    totalAmount: number;
    totalAdminFee: number;
    count: number;
    startDate: string;
    endDate: string;
  };
}

/** Zeros for a page that arrived without its summary. */
export const EMPTY_PAID_COLLECTION_SUMMARY: PaidCollectionsPage['summary'] = {
  totalAmount: 0,
  totalAdminFee: 0,
  count: 0,
  startDate: '',
  endDate: '',
};

export interface GetPaidCollectionsParams {
  page?: number;
  size?: number;
  /**
   * Inclusive WIB calendar dates filtering `paidAt` — when the money landed.
   * Each defaults to today on its own.
   */
  dateFrom?: string;
  dateTo?: string;
  /**
   * The payments-menu filter panel. The dashboard tab sends none of these and
   * keeps the plain window feed. `search` spans the transaction code, the
   * payment method, the rental code, the mitra name/code and the plat nomor —
   * which is why there is no separate method filter: the method is free text
   * (a core-payment channel code), so a fixed option list would go stale the
   * day a channel is added.
   */
  purpose?: CollectionPurpose[];
  search?: string;
}

/**
 * `POST /collections/:collectionID/simulate-payment` (dev/QA only) answers 202
 * with an acknowledgement, not a collection row — the settlement itself lands
 * asynchronously via the capture path, so callers re-read rather than trust
 * this body. Both fields are optional: the contract only fixes the status code.
 */
export interface SimulatePaymentResponse {
  collectionID?: string;
  accepted?: boolean;
}

// ---------------------------------------------------------------------------
// Charges — the daily charge ledger
// ---------------------------------------------------------------------------

/**
 * What a charge is for. RENT is revenue; the two DEPOSIT_* types accumulate
 * the refund liability. RTO rentals also charge RENT — payments are legally
 * rent until the tenor completes (TRD R4).
 */
export enum ChargeType {
  RENT = 'RENT',
  DEPOSIT_DP = 'DEPOSIT_DP',
  DEPOSIT_INSTALLMENT = 'DEPOSIT_INSTALLMENT',
}

export const CHARGE_TYPE_LABEL: Record<ChargeType, string> = {
  [ChargeType.RENT]: 'Sewa',
  [ChargeType.DEPOSIT_DP]: 'DP',
  [ChargeType.DEPOSIT_INSTALLMENT]: 'Cicilan Deposit',
};

/**
 * Ledger status. "Overdue" is deliberately not a member: it is derived
 * (PENDING with a `chargeDate` before today, WIB), which is the same
 * definition the `arrears` figures and the list's `isOverdue` filter use — so
 * a PENDING row is what ops are looking at when they follow the arrears
 * number into this ledger.
 */
export enum ChargeStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  WAIVED = 'WAIVED',
  CANCELLED = 'CANCELLED',
}

export const CHARGE_STATUS_LABEL: Record<ChargeStatus, string> = {
  [ChargeStatus.PENDING]: 'Belum Bayar',
  [ChargeStatus.PAID]: 'Lunas',
  [ChargeStatus.WAIVED]: 'Gratis',
  [ChargeStatus.CANCELLED]: 'Dibatalkan',
};

/**
 * How a charge was settled. BALANCE is the ordinary case, not an anomaly: the
 * nightly billing engine drains residual prepaid balance without recording a
 * collection, so those days name no single payment.
 */
export type ChargePaidSource = 'COLLECTION' | 'BALANCE';

/** The collection that settled a charge — thin by design, code + id only. */
export interface ChargeCollectionRef {
  id: string;
  /** Transaction code, e.g. "TRX-260730-A7K2M9". */
  code: string;
}

/** Row from `GET /rental/:id/charges`. */
export interface RentalCharge {
  id: string;
  /** The day being billed, `YYYY-MM-DD` — not when the row was created. */
  chargeDate: string;
  type: ChargeType;
  amount: number;
  status: ChargeStatus;
  /** Null unless PAID. */
  paidAt: string | null;
  /**
   * Who paid, from the settling collection's creator — `driver:<id>` today,
   * since every collection is minted by a driver action. Null whenever
   * `paidSource` is not `COLLECTION`.
   */
  paidBy: string | null;
  /** How the day was settled. Null while unpaid. */
  paidSource: ChargePaidSource | null;
  /**
   * Which payment settled this day. Null for anything unpaid, and also for a
   * WAIVED day — a free day is settled by a waiver, not by money.
   */
  collection: ChargeCollectionRef | null;
  /**
   * Provenance, not accounting: true when a free-day waiver produced this
   * charge. Distinct from `status === 'WAIVED'`, which is where the money
   * went — an admin write-off reaches WAIVED with no waiver behind it and is
   * not a free day. `summary.totalWaived` is the status-based figure.
   */
  freeDay: boolean;
  /** When the charge row was written, ISO — not the day being billed. */
  createdAt: string;
  /** `SYSTEM_BILLING` for the nightly engine, an admin actor for a manual day. */
  createdBy: string;
}

/**
 * Ledger totals for the **whole rental**, not for the page or the filter —
 * `totalPending` is the arrears figure the detail screen shows, so it has to
 * stay stable while ops narrow the table down.
 */
export interface RentalChargeSummary {
  totalCharged: number;
  totalPaid: number;
  totalPending: number;
  totalWaived: number;
}

/** Zeros for a charges page that answers without a summary. */
export const EMPTY_CHARGE_SUMMARY: RentalChargeSummary = {
  totalCharged: 0,
  totalPaid: 0,
  totalPending: 0,
  totalWaived: 0,
};

/**
 * `GET /rental/:id/charges` — a paginated page carrying its summary, so the
 * screen does not need a second request for the header figures.
 */
export interface RentalChargePage {
  items: RentalCharge[];
  summary: RentalChargeSummary;
  pagination: ApiPagination;
}

export interface GetChargesParams {
  page?: number;
  size?: number;
  /** Comma-joined server-side; multiple values allowed. */
  status?: ChargeStatus[];
  /** Comma-joined server-side; multiple values allowed. */
  type?: ChargeType[];
  /** Filters `chargeDate` — the billed day, `YYYY-MM-DD`. */
  dateFrom?: string;
  dateTo?: string;
  /** Hari-hari yang diselesaikan satu pembayaran — id collection-nya. */
  collectionID?: string;
}

// ---------------------------------------------------------------------------
// Status timeline
// ---------------------------------------------------------------------------

/**
 * Which lifecycle a timeline row belongs to. Load-bearing, not decoration:
 * CANCELLED is a valid value in both RentalStatus and RentalPaymentStatus, so
 * a from/to pair alone is ambiguous — always read statusType for context.
 */
export enum TimelineStatusType {
  RENTAL = 'RENTAL',
  PAYMENT = 'PAYMENT',
  ESCALATION = 'ESCALATION',
}

/**
 * A raw transition, not a render-ready label — the API deliberately does not
 * fix the wording, so the client owns it.
 */
/** A possession transition. Same shape as RentalTimelineEntry minus statusType. */
export interface HandoverTimelineEntry {
  ID: string;
  fromStatus: string | null;
  toStatus: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  createdBy?: string | null;
}

export interface RentalTimelineEntry {
  ID: string;
  statusType: TimelineStatusType;
  fromStatus: string | null;
  toStatus: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  createdBy?: string | null;
}

// ---------------------------------------------------------------------------
// Handover / return evidence
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/**
 * Which stage of the rental minted this copy of the agreement. Three separate
 * printed artefacts, not three versions of one: `BOOKING` carries the terms
 * with the serah-terima sections still blank, `HANDOVER` adds the pickup
 * checklist and signatures, `RETURN` adds the return's.
 */
export type RentalDocumentStage = 'BOOKING' | 'HANDOVER' | 'RETURN';

/** One issued copy of the "Paket Dokumen Sewa Kendaraan B2C". */
export interface RentalAgreementDocument {
  type: RentalDocumentStage;
  /** `{first8 rentalID}/DPI/RENT/{roman}/{yy}` — the same across stages. */
  number: string;
  /** Public object URL in the rent bucket; open it directly. */
  link: string;
  templateVersion: number;
  generatedAt: string;
}

/**
 * The rent's whole paper trail. Three documents, reachable three different
 * ways — which is exactly why they arrive in one block:
 *
 * - Perjanjian Sewa Kontrak — `rentalAgreements`, issued PDFs with URLs.
 * - BAST — `bast`, mirrored off the handover's two legs.
 * - Surat Penjamin — no field at all. It is a blank FORM rendered per request
 *   and never stored, so it has no link and no availability flag; the screen
 *   always offers `downloadRentalGuarantorLetter` and surfaces its 403.
 */
export interface RentalDocuments {
  /** Most complete stage first; empty until the generator has run. */
  rentalAgreements: RentalAgreementDocument[];
  bast: {
    handoverLink: string | null;
    returnLink: string | null;
  };
  /**
   * A checklist correction's reissue, derived on the server: `pending` lists
   * the copies that still predate the newest correction (`requestedAt`).
   * Absent on a backend older than fleet #559.
   */
  reissue?: {
    requestedAt: string | null;
    pending: ('BAST' | 'HANDOVER' | 'RETURN')[];
  };
}

/**
 * The row behind `rental.handoverID`; null until a handover exists. Both legs
 * share one row, so every `return*` field stays null until the unit is back.
 */
export interface RentalHandoverRecord {
  ID: string;
  status: HandoverStatus;
  /**
   * ACTUAL pickup, and null until one happens — the row exists from DP capture.
   * The planned slot is `scheduledHandoverDate`; do not read one for the other.
   */
  handoverDate: string | null;
  scheduledHandoverDate: string | null;
  scheduledReturnDate: string | null;
  odometer: number | null;
  /** Nullable until the BAST document exists (TRD D1) — never a sentinel. */
  bastLink: string | null;
  notes: string | null;
  unitEquipment: string[] | null;
  photos: string[] | null;
  signatureDriverURL: string | null;
  signatureOpsURL: string | null;

  returnDate: string | null;
  returnOdometer: number | null;
  returnCondition: string | null;
  returnNotes: string | null;
  returnUnitEquipment: string[] | null;
  returnPhotos: string[] | null;
  returnBastLink: string | null;
}

export interface RentalWaiver {
  id: string;
  waiverDate: string;
  source?: string;
  reason?: string | null;
}

export interface RentalReturnRequest {
  id: string;
  requestedAt: string;
  returnDate: string;
  status: 'REQUESTED' | 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';
}

/**
 * Itemized damage deduction, priced at settlement (never at the physical
 * return) — see TRD_CHANNEL_STATUS_DAMAGE_OUTSTANDING.md Part 2. Empty until
 * a SETTLE proposal has priced any.
 */
export interface RentalDeductionItem {
  id: string;
  /** The endpoint returns uppercase `ID` (Drizzle PK casing) — normalised in
   *  the service layer, kept here so the raw response still type-checks. */
  ID?: string;
  description: string;
  amount: number;
  partRef?: string | null;
  evidence?: string[] | null;
  createdAt: string;
  createdBy: string;
}

export interface RentalRemoteCommand {
  id?: string;
  command: 'SHUTDOWN' | 'ENABLE';
  trigger: 'ESCALATION' | 'PAYMENT_SETTLED' | 'OPS';
  result?: string;
  createdAt: string;
}

/** Derived plan stored on the rental at creation. */
export interface RentalPlan {
  dpAmount: number;
  installmentCount: number;
  installmentAmount: number;
  /** RTO only. */
  tenorEffectiveDays: number | null;
}

/**
 * Commercial terms frozen at creation (TRD C8) — a copy of the package's
 * §4.1 fields. Modelled as a nested object; every field is optional because
 * the contract documents it only as "full terms snapshot (§4.1 copy)".
 */
export interface RentalTermsSnapshot {
  dailyPrice?: number;
  deposit?: number;
  depositNonRefundable?: number;
  minDp?: number;
  maxInstallments?: number;
  minRentalDays?: number;
  freeDayMinTenureDays?: number;
  freeDayDates?: number[];
  minReturnNoticeDays?: number;
  maxPrepayDays?: number;
  contractType?: RentType;
  content?: PackageContentBlock[];
}

export interface RentalDetail extends RentalListItem {
  termsSnapshot?: RentalTermsSnapshot;
  matchSnapshot?: Record<string, unknown>;
  plan?: RentalPlan;

  /**
   * Fixed-size replacements for the old `charges[]` / `payments[]` arrays,
   * which grew a row per day while the screen only ever rendered two numbers
   * from them. The payment rows moved to `GET /rental/:id/collections`.
   */
  /**
   * `paidDays` counts installment charges the daily engine has minted AND
   * settled; `coveredDays` adds the ones the prepaid balance already accounts
   * for. Render `coveredDays` — a driver who prepaid a week reads as 0 on
   * `paidDays` until the nightly job catches up.
   */
  depositInstallment?: {
    paidDays: number;
    coveredDays: number;
    totalDays: number;
  };
  /**
   * `elapsedDays` is null before handover. `paidDays`/`coveredDays`/
   * `remainingDays` are the same balance projection the deposit block uses,
   * and are null on RENT contracts (no effective tenor).
   */
  tenor?: {
    elapsedDays: number | null;
    totalDays: number | null;
    paidDays?: number | null;
    coveredDays?: number | null;
    remainingDays?: number | null;
  };

  /** Oldest first. */
  timeline?: RentalTimelineEntry[];
  /**
   * The possession trail, kept separate from `timeline` rather than merged:
   * these rows have no `statusType` (a handover has one status dimension), and
   * merging would need a synthetic ordering across two tables whose clocks move
   * independently. Rendered as a second column.
   */
  handoverTimeline?: HandoverTimelineEntry[];
  /** Every pre-pickup unit swap, oldest first (fleet #553). */
  reassignments?: RentalReassignment[];
  handover?: RentalHandoverRecord | null;

  waivers?: RentalWaiver[];
  returnRequests?: RentalReturnRequest[];
  remoteCommands?: RentalRemoteCommand[];
  deductions?: RentalDeductionItem[];
  /**
   * The debt this rental's settlement left behind, or null when it left none.
   *
   * Read-only here and deliberately thin: the obligation belongs to the DRIVER
   * (it outlives this rental), so the outstanding screen owns it and this is
   * just the crossing point between the two. `ID` is uppercase — it is the
   * outstanding's own PK, straight off the row.
   */
  outstanding?: {
    ID: string;
    status: DriverOutstandingStatus | string;
    amountOriginal: number;
    amountOutstanding: number;
  } | null;

  /**
   * Money the driver has put in, and what would come back — derived live by the
   * server, not read off a column, so it exists BEFORE any settlement has priced
   * anything. The same block the driver app and both cancel responses render.
   *
   * `stage` is load-bearing, not decoration: the same arithmetic is three
   * different promises. Never render `estimatedNet` without it — an `ESTIMATE`
   * is a ceiling (damage is unknown until the unit is back, and the figure moves
   * daily as installments settle), and ops quoting it to a driver as settled is
   * exactly what the label prevents.
   *
   * Optional because the field ships with the 2026-08-10 backend; older
   * deployments omit it.
   */
  refundable?: {
    stage: 'PRE_HANDOVER' | 'ESTIMATE' | 'FINAL';
    depositPaid: number;
    prepaidBalance: number;
    nonRefundable: number;
    /** Charged across PAID collections. NEVER refunded — see adminFeeRefundable. */
    adminFeePaid: number;
    adminFeeRefundable: false;
    deductions: number;
    estimatedGross: number;
    /** The disbursement fee, deducted from every payout. */
    mdrFee: number;
    estimatedNet: number;
  } | null;

  depositPaidTotal?: number | null;
  depositRefundAmount?: number | null;
  refundInstructedAt?: string | null;
  settlementNotes?: string | null;
  carriedFromRentalID?: string | null;

  /**
   * Every document this rental holds, in one block. Optional because the field
   * ships with the 2026-09-02 backend; older deployments omit it and the
   * screen falls back to the BAST link on `handover`.
   */
  documents?: RentalDocuments | null;

  /**
   * Jeda Tagihan. `current` is the open pause plus the rung it froze; history
   * is every pause, oldest first. Optional until every backend ships it.
   */
  billingPause?: {
    current:
      | (RentalBillingPause & {
          escalationStatus: EscalationStatus;
          escalationDaysLate: number;
        })
      | null;
    history: RentalBillingPause[];
  };

  /** BAST links; the contract names them without fixing the key. */
  bastUrl?: string | null;
  bastLink?: string | null;

  /** Handover extras, present once the ops handover has been recorded. */
  unitEquipment?: string[] | null;
  notes?: string | null;
  odometer?: number | null;
}

export interface GetRentalsParams {
  page?: number;
  size?: number;
  /**
   * Comma-joined server-side; multiple values allowed — the controller splits
   * it into `statuses[]`.
   */
  status?: RentalStatus[];
  /**
   * Possession rung, comma-joined server-side. The Waiting Handover and
   * Waiting Return tabs filter on THIS, not on `status` — the rental is ACTIVE
   * for both. The Active tab sends both, because it is the conjunction.
   */
  handoverStatus?: HandoverStatus[];
  /** Comma-joined server-side; multiple values allowed. */
  escalationStatus?: EscalationStatus[];
  /** Comma-joined server-side; multiple values allowed. */
  paymentStatus?: RentalPaymentStatus[];
  packageID?: string;
  driverID?: string;
  modelID?: string;
  vehicleID?: string;
  /**
   * Derived server-side, not stored: `true` returns only rentals with at
   * least one PENDING charge dated before today, `false` only those without.
   * Omit for no filter — an unrecognised value is ignored rather than read as
   * `false`. Shares one definition with the `arrears` figures, so the filter
   * and the numbers cannot disagree.
   */
  isOverdue?: boolean;
  /**
   * ONE arrears-day bucket, narrower than `isOverdue` and independent of it
   * (every bucket already implies at least one overdue day, so sending both is
   * redundant rather than contradictory). Omit for no bucket filter; the API
   * 422s an unknown value rather than silently returning every overdue rental,
   * which would read as "nobody is 3 days late".
   */
  arrearsBucket?: ArrearsBucket;
  /**
   * The "Lainnya" bucket, as a NEGATIVE the server owns: `true` returns the
   * rentals whose status is NOT one of the five that have a tab of their own.
   * A dedicated flag rather than an IN-list so off-enum and newly added
   * statuses still surface, and so the tab and its badge — which the counts
   * endpoint derives the same way — cannot disagree.
   */
  isOther?: boolean;
  contractType?: RentType;
  search?: string;
  /**
   * The waiting tabs' date range, `YYYY-MM-DD`.
   *
   * Two independent pairs, not one start/end, because "by date" means a
   * different column per tab: Waiting Handover is due on the scheduled pickup,
   * Waiting Return on the scheduled return. Sending the pair that matches the
   * tab is what keeps the question unambiguous — and on the counts endpoint the
   * server narrows only that tab's badge with it.
   */
  scheduledHandoverStartDate?: string;
  scheduledHandoverEndDate?: string;
  scheduledReturnStartDate?: string;
  scheduledReturnEndDate?: string;
  /**
   * `true` returns only rentals with a refund payout stuck at NOT_INSTRUCTED
   * or FAILED — the dashboard's "refund macet" queue. Any other value means
   * no filter.
   */
  refundStuck?: boolean;
  /** One hub, off `rental.hub_id`. Shared with the stats badges. */
  hubID?: number;
  /** true: the handover has a repossession case that is not CLOSED; false: none. */
  hasRepossession?: boolean;
  /**
   * A free arrears-day range, inclusive, either end open — the same day count
   * as `arrearsBucket`. The API 422s a bad or inverted range.
   */
  arrearsDaysMin?: number;
  arrearsDaysMax?: number;
  /**
   * `'arrears'` orders by total overdue amount, largest first (the
   * dashboard's Overdue queue). Any other value is a 422; omit for the
   * default newest-first.
   */
  sort?: 'arrears';
}

/**
 * GET /rental/dashboard-summary — the rent dashboard's money aggregates.
 * `rate` is null when nothing was due this month: render "—", never 0%.
 * The overdue definition matches the list's `isOverdue`, so the card, the
 * tab badge, and the queue rows always agree.
 */
export interface RentalDashboardSummary {
  /**
   * EVERY block is nullable, and that is the contract, not an accident. The
   * backend runs its six aggregates under `Promise.allSettled`, so one slow
   * query returns null for its own block instead of 500ing the screen.
   *
   * Render a null block as "gagal memuat" with a retry, NEVER as 0 or "-". A
   * dashboard that shows 0 when it means "I do not know" tells ops there is
   * nothing to do, which is the exact bug this nullability exists to make
   * impossible to reintroduce.
   */
  collectionRate: RentalDashboardCollectionRate | null;
  /**
   * The equally long window immediately before `collectionRate`'s, so the two
   * are the same length AND equally matured. Comparing a running window against
   * a completed period always flatters the completed one.
   */
  previousCollectionRate: RentalDashboardCollectionRate | null;
  /** Today-snapshot, unaffected by the range. */
  arrears: {
    totalAmount: number;
    rentalCount: number;
  } | null;
  /**
   * Of the units on the road right now, how many are late enough to lose the
   * unit. A headcount, not money, and not windowed. `thresholdDays` is the cut
   * actually applied, read live from setting_rent_escalation.
   */
  rentalHealth: {
    rate: number | null;
    lateRentalCount: number;
    totalRentalCount: number;
    thresholdDays: number;
    asOf: string;
  } | null;
  /**
   * Contracts opened and closed inside the window. `net` is computed by the
   * backend so nobody can disagree about the sign.
   */
  netContracts: {
    newCount: number;
    endedCount: number;
    net: number;
    startDate: string;
    endDate: string;
  } | null;
  /**
   * Units awaiting repossession. `oldestDays` is null when nothing is stuck, and
   * also when a stuck rental has no history row to anchor on — it still counts.
   */
  repoStuck: {
    count: number;
    oldestDays: number | null;
  } | null;
}

export interface RentalDashboardCollectionRate {
  /** paidAmount / dueAmount, null when nothing was due: 0/0 is "no data". */
  rate: number | null;
  /**
   * "Total Collection" — charges DATED in the window that are now paid.
   * Accrual, not cash: a charge dated the 1st and paid the 18th counts here
   * under the 1st. NOT the same figure as the Pembayaran menu, which windows on
   * paid_at.
   */
  paidAmount: number;
  /** "Target Collection" — due (PAID + PENDING) and already matured. */
  dueAmount: number;
  /** The window actually applied. Label from this, never re-derive it. */
  startDate: string;
  endDate: string;
}

/**
 * Per-queue stats for the rent dashboard's action tabs — a today-snapshot, so
 * unaffected by the collection cards' date window. The numbers overlap (an
 * overdue rental can also be escalated) and must never be summed.
 *
 * "Pembayaran Hari Ini" is absent by design: its totals come back with the
 * payments feed itself.
 */
export interface RentalDashboardQueues {
  /**
   * The WIB window the two scheduled queues were counted over, fixed by the
   * server. Feed THESE bounds to the list endpoint — deriving the window again
   * on this side would put one constant in two repositories, and the stat strip
   * would eventually describe different days from its own rows.
   */
  startDate: string;
  endDate: string;
  queues: {
    /**
     * Named "today" for contract reasons only: the window has not been a single
     * day since the queues widened. The bounds above say what was counted.
     */
    handoverToday: { count: number };
    returnToday: { count: number };
    /** `totalAmount` is the fleet-wide arrears total behind the Overdue card. */
    overdue: { count: number; totalAmount: number };
    /**
     * Split by rung: manual shutdown and repossession are different actions.
     * A now-snapshot — these do NOT move when the window moves.
     */
    escalation: {
      shutdownPending: number;
      repoRequested: number;
    };
    /** `count` is rentals (what the queue lists); the rest describe the rows. */
    refundFailed: {
      count: number;
      totalAmount: number;
      notInstructed: number;
      failed: number;
    };
  };
}

/** Both bounds absent means the server's own fixed window. */
export interface GetRentalDashboardQueuesParams {
  /** Legacy single-day form. Sending it with a range is a 422. */
  date?: string;
  startDate?: string;
  endDate?: string;
}

/** Charge-date window; both absent = current WIB month-to-date. */
export interface GetRentalDashboardSummaryParams {
  startDate?: string;
  endDate?: string;
}

/**
 * GET /rental/:id/reassign-candidates — the units REASSIGN_VEHICLE would
 * accept. Feed the "Ganti Unit" picker from this, never from the fleet
 * vehicle list: these are pre-filtered by the rental's own package rules.
 */
export interface ReassignCandidate {
  id: string;
  platNomor: string;
  hubName: string | null;
  condition: string;
  odometer: number | null;
  /**
   * The unit fails the package rule on odometer, age or condition only: the
   * swap takes it with an `overrideReason`. Absent on backends before #553.
   */
  requiresOverride?: boolean;
  /** The failures being waived; empty when the unit matches. */
  reasons?: PackageVehicleReason[];
}

export interface ReassignCandidates {
  items: ReassignCandidate[];
  /**
   * Informational since fleet #553: the swap ignores the package's status,
   * because the rental's terms were frozen at booking.
   */
  packageStatus?: PackageStatus | null;
}

/** One pre-pickup unit swap, from `reassignments` on the rental detail. */
export interface RentalReassignment {
  ID: string;
  fromVehicle: { ID: string; platNomor: string };
  toVehicle: { ID: string; platNomor: string };
  ruleID: string | null;
  /** Null unless the unit failed the rule on odometer, age or condition. */
  overrideReason: string | null;
  waived: PackageVehicleReason[] | null;
  /** RTO only; null on RENT. */
  tenorBefore: number | null;
  tenorAfter: number | null;
  createdBy: string;
  createdAt: string;
}

/**
 * Non-tab filters for the counts endpoint.
 *
 * `page`/`size` are absent because a count is not paginated, and `status`,
 * `isOverdue` and `isOther` are absent because those *are* the tabs — sending
 * them would make every tab report the count of the tab you are already
 * standing on.
 *
 * Everything else is the list's own filter type unchanged, so the badge and the
 * table it labels are provably sent the same filters.
 */
export type GetRentalStatsParams = Omit<
  GetRentalsParams,
  | 'page'
  | 'size'
  | 'status'
  | 'handoverStatus'
  | 'isOverdue'
  | 'isOther'
  // Counted, not filtered: the endpoint answers all four buckets at once, and
  // sending one would narrow the badges to the bucket already selected.
  | 'arrearsBucket'
  | 'arrearsDaysMin'
  | 'arrearsDaysMax'
>;

/**
 * Row count per Rent Activity tab, under the same non-tab filters the list is
 * showing.
 *
 * The six status keys partition across TWO columns — `waitingHandover` and
 * `waitingReturn` come from handover.status, `ended` and `completed` from
 * rental.status, and `active` is the conjunction of both. Every rental belongs
 * to exactly one, so none is missing from the tabs. `others` is the catch-all
 * that makes that true: PRE_ORDER, REQUEST_BOOKING and CANCELLED.
 *
 * These still do NOT add up to a fleet total: `overdue` is a derived flag
 * rather than a status, so it sits on top of the partition and an overdue
 * rental is also counted under `ongoing` or `waitingReturn`. Summing
 * double-counts.
 */
export interface RentalStats {
  /** Contract ACTIVE *and* unit out with the driver — the narrow reading. */
  active: number;
  /** These two key on handover.status; the rental is ACTIVE across both. */
  waitingHandover: number;
  waitingReturn: number;
  overdue: number;
  /** The unit came back and the rental closed. */
  ended: number;
  /** RTO tenor filled — the unit is the driver's now. */
  completed: number;
  others: number;
}

/**
 * The tab counts and the arrears-day counts, as the endpoint answers them.
 *
 * Kept apart from `RentalStats` because the four bucket counts are NOT tabs:
 * the Rent Activity screen keys its tab bar off `keyof RentalStats`, and a
 * bucket landing in that union would demand a tab, a query and an empty state
 * it has no business having. They are a breakdown of `overdue`, which is
 * exactly what the chips above that tab show.
 */
export interface RentalStatsWithArrears extends RentalStats {
  /**
   * Counted under the same boundary table `arrearsBucket` filters by, so a chip
   * labelled "3 hari" lists exactly what it counted. These four partition
   * `overdue`: every overdue rental is behind by some number of days.
   */
  overdueDay1: number;
  overdueDay2: number;
  overdueDay3: number;
  overdueOver3: number;
}

/**
 * How many days behind a rental is, as the four rows the collections monitor
 * watches. A day is a distinct `chargeDate`: a day carrying both a RENT and a
 * DEPOSIT_INSTALLMENT charge is one day behind, and a rental that missed the
 * 1st and the 3rd but paid the 2nd is two days behind, not three.
 */
export enum ArrearsBucket {
  D1 = 'D1',
  D2 = 'D2',
  D3 = 'D3',
  D3_PLUS = 'D3_PLUS',
}

export const ARREARS_BUCKET_LABEL: Record<ArrearsBucket, string> = {
  [ArrearsBucket.D1]: '1 hari',
  [ArrearsBucket.D2]: '2 hari',
  [ArrearsBucket.D3]: '3 hari',
  [ArrearsBucket.D3_PLUS]: '> 3 hari',
};

/**
 * Which `RentalStats` key counts each bucket, so a chip's label and its filter
 * are read off one table instead of two parallel switches.
 */
export const ARREARS_BUCKET_STAT_KEY: Record<
  ArrearsBucket,
  keyof Omit<RentalStatsWithArrears, keyof RentalStats>
> = {
  [ArrearsBucket.D1]: 'overdueDay1',
  [ArrearsBucket.D2]: 'overdueDay2',
  [ArrearsBucket.D3]: 'overdueDay3',
  [ArrearsBucket.D3_PLUS]: 'overdueOver3',
};

/** Worst last, the order the chips are shown in. */
export const ARREARS_BUCKET_ORDER: ArrearsBucket[] = [
  ArrearsBucket.D1,
  ArrearsBucket.D2,
  ArrearsBucket.D3,
  ArrearsBucket.D3_PLUS,
];

export interface RentalPaginated<T> {
  items: T[];
  pagination: ApiPagination;
}

/** One action per PATCH call. */
export enum RentalAction {
  HOLD_ESCALATION = 'HOLD_ESCALATION',
  RESUME_ESCALATION = 'RESUME_ESCALATION',
  /** Ops override of the pickup slot or return date. */
  UPDATE_DATES = 'UPDATE_DATES',
  CANCEL = 'CANCEL',
  FORCE_EARLY_RETURN = 'FORCE_EARLY_RETURN',
  SETTLE = 'SETTLE',
  MARK_SHUTDOWN_DONE = 'MARK_SHUTDOWN_DONE',
  REASSIGN_VEHICLE = 'REASSIGN_VEHICLE',
  /** Withdraws a PENDING rent prepayment; payload `{ collectionID }`. */
  CANCEL_PAYMENT = 'CANCEL_PAYMENT',
}

/**
 * Payload for UPDATE_DATES. Every field is optional — send only what changes.
 * The backend re-validates against the hub's active days, so an invalid date
 * comes back as a 422 rather than being silently accepted.
 */
export interface UpdateRentalDatesPayload {
  slotDate?: string;
  slotStartTime?: string;
  slotEndTime?: string;
  returnDate?: string;
}

export interface RentalActionBody {
  action: RentalAction;
  /**
   * Shape varies per action, so it stays loose here — the named helpers in
   * `services/api/rentals.ts` are where each action's payload is typed.
   */
  payload?: unknown;
}

/** `POST /settlements/:rentalId/approve` — CHECKER side (TRD O4). */
export interface SettlementApprovalResult {
  rentalID: string;
  refund: { amount: number; channel: string; payoutID: string };
  refundInstructedAt: string;
}

// ---------------------------------------------------------------------------
// Free days — waivers
// ---------------------------------------------------------------------------

/**
 * `POST /waivers/bulk`. At least one of the two scope arrays should be set;
 * an empty scope grants nothing rather than granting to everyone.
 *
 * `grantedBy` is absent here on purpose — the service layer stamps it, so no
 * call site can forget the field the API requires.
 */
export interface BulkCreateWaiversBody {
  /** The single day being waived, `YYYY-MM-DD`. */
  date: string;
  scope: { packageIDs?: string[]; rentalIDs?: string[] };
  reason?: string;
}

/**
 * Rentals that did not clear the per-package tenure gate are reported rather
 * than silently dropped, so the UI can say "41 granted, 9 skipped" instead of
 * implying 9 failures.
 */
export interface BulkCreateWaiversResult {
  created: number;
  skippedIneligible: number;
}

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------

export enum ReturnRequestStatus {
  REQUESTED = 'REQUESTED',
  CONFIRMED = 'CONFIRMED',
  COMPLETED = 'COMPLETED',
  NO_SHOW = 'NO_SHOW',
  CANCELLED = 'CANCELLED',
}

export const RETURN_REQUEST_STATUS_LABEL: Record<ReturnRequestStatus, string> =
  {
    [ReturnRequestStatus.REQUESTED]: 'Requested',
    [ReturnRequestStatus.CONFIRMED]: 'Confirmed',
    [ReturnRequestStatus.COMPLETED]: 'Completed',
    [ReturnRequestStatus.NO_SHOW]: 'No Show',
    [ReturnRequestStatus.CANCELLED]: 'Cancelled',
  };

/**
 * Row from `GET /return-requests`. Deliberately thin — no driver or vehicle, so
 * a queue screen has to resolve those itself. Note the uppercase `ID`, which is
 * what this endpoint actually returns.
 */
export interface MgmtReturnRequest {
  ID: string;
  /** The request belongs to the handover; possession is its subject. */
  handoverID: string;
  /** Resolved server-side by joining rental on handover_id, for navigation. */
  rentalID: string | null;
  requestedAt: string;
  returnDate: string;
  status: ReturnRequestStatus;
}

export interface GetReturnRequestsParams {
  /** `YYYY-MM-DD`; omitted means every date. */
  date?: string;
  status?: ReturnRequestStatus;
  page?: number;
  /** Server default is 50 here, not the usual 10. */
  size?: number;
}

/** Row from `GET /pre-orders`, oldest first — the queue is FIFO. */
export interface MgmtPreOrder {
  id: string;
  driver: RentalDriver;
  packageID: string;
  dpAmount: number;
  dpPaid: boolean;
  /** 1-based position across the whole queue, not within a page. */
  queuePosition: number;
  /** When the offer lapses and the unit passes to the next in line. */
  responseDeadline: string | null;
  createdAt: string;
}

export interface GetPreOrdersParams {
  /** Required — the queue is read per package. */
  packageID: string;
  /** Filters on the rental's payment status, e.g. PAID for DP-settled rows. */
  status?: RentalPaymentStatus;
}

/**
 * Refunds ride the disbursement rail, so a fixed MDR fee is always deducted
 * — a "100% refund" is 100% of gross, net of that fee (M4/B5 revised).
 */
export interface RefundBreakdown {
  gross: number;
  mdrFee: number;
  net: number;
}

/** Fixed disbursement fee deducted from every refund. */
export const REFUND_MDR_FEE = 2500;
